import { useAtom } from 'jotai';
import { z } from 'zod';

import {
	createLog,
	emailSchema,
	legalNameSchema,
	LogSeverity,
	phoneSchema,
} from '@ekhodealer/ekho-common';
import { Box, Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateBusinessDetails } from '../../api/configuration/business-details/useUpdateBusinessDetails';
import { forceValidateBusinessIdentityAtom } from '../../atoms/onboardingValidationAtoms';

/** Fields that may exist on the underlying data but not on the base ServicesProvider class. */
interface IdentityFields {
	name?: string;
	legalName?: string;
	email?: string;
	phone?: string;
}

/**
 * Helper function to get initial form data from ServicesProvider.
 * `legalName` lives on the underlying data object (PayableServicesProvider), not on the base class.
 */
function getDefaultValues(servicesProvider: { toObject?: () => Record<string, unknown> } | undefined | null): {
	name: string;
	legalName: string;
	email: string;
	phone: string;
} {
	if (!servicesProvider) {
		return {
			name: '',
			legalName: '',
			email: '',
			phone: '',
		};
	}

	const data = (servicesProvider.toObject?.() ?? servicesProvider) as unknown as IdentityFields;
	return {
		name: data.name || '',
		legalName: data.legalName || '',
		email: data.email || '',
		phone: data.phone || '',
	};
}

/**
 * Helper function to get update payload from form data
 */
function getServicesProviderFromFormData(
	data: { name: string; legalName: string; email: string; phone: string },
	currentServicesProvider: { toObject?: () => Record<string, unknown> },
): Record<string, unknown> {
	const currentData = (currentServicesProvider.toObject?.() ?? currentServicesProvider) as unknown as IdentityFields;
	return {
		name: data.name || currentData.name,
		legalName: data.legalName || currentData.legalName,
		email: data.email || currentData.email,
		phone: data.phone || currentData.phone,
	};
}

const businessIdentitySchema = z.object({
	name: z.string().min(1, 'Display name is required'),
	legalName: legalNameSchema,
	email: emailSchema,
	phone: phoneSchema,
});

type BusinessIdentityFormData = z.infer<typeof businessIdentitySchema>;

export function BusinessIdentityForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateBusinessDetailsMutation = useUpdateBusinessDetails();
	const [forceValidation, setForceValidation] = useAtom(forceValidateBusinessIdentityAtom);

	// Handle form submit
	const handleSubmit = async (
		data: BusinessIdentityFormData,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) {
			return { isSuccess: false };
		}

		try {
			const updatedServicesProvider = getServicesProviderFromFormData(data, servicesProvider);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: updatedServicesProvider,
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update business identity: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			// TODO: Handle error state (show user feedback, revert changes, etc.)
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='business-identity'
				schema={businessIdentitySchema}
				defaultValues={getDefaultValues(servicesProvider)}
				onSubmit={handleSubmit}
				forceValidation={forceValidation}
				title='Company info'
				titleLevel='subgroup'
				titleWeight='semibold'
				showSaveAndClear>
				<Form.Field.Text
					schemaField='name'
					labelText='Display Name'
					required
					size='large'
					placeholder='E.g., BG Motorsports'
				/>
				<Form.Field.Text
					schemaField='legalName'
					labelText='Business Name'
					required
					size='large'
					placeholder='E.g., BG Motorsports LLC'
				/>
				<Form.Field.Text
					schemaField='email'
					labelText='Email'
					descriptionText='Used to notify your business of outstanding actions.'
					required
					size='large'
					type='email'
					placeholder='Enter email'
				/>
				<Form.Field.Text
					schemaField='phone'
					labelText='Phone'
					descriptionText='Used to show your support line to buyers.'
					required
					size='large'
					type='tel'
					placeholder='Enter phone'
				/>
			</Form>
		</Box>
	);
}

export default BusinessIdentityForm;
